<template>
  <div class="signup-step">
    <h2>
      {{ t('step5_title_prefix') }}<span class="grad">{{ t('step5_title_grad') }}</span
    >{{ t('step5_title_suffix') }}
    </h2>
    <p class="sub">{{ t('step5_sub') }}</p>

    <div class="signup-field">
      <label
        class="field-label"
        id="signup-name-label"
      >{{ t('lbl_name') }}</label
      >
      <input
        id="signup-name"
        :value="name"
        type="text"
        class="signup-input"
        aria-labelledby="signup-name-label"
        :class="{ error: errors.name }"
        :placeholder="t('ph_name')"
        maxlength="30"
        @input="$emit('update:name', $event.target.value)"
      />
      <div
        v-if="errors.name"
        class="err"
      >
        <span>{{ errors.name }}</span>
      </div>
      <div
        v-else
        class="field-note"
      >
        {{ t('helper_name') }}
      </div>
    </div>

    <template v-if="!isContinuing">
      <div class="signup-field">
        <label
          class="field-label"
          id="signup-email-label"
        >{{ t('lbl_email') }}</label
        >
        <input
          id="signup-email"
          :value="email"
          type="email"
          class="signup-input"
          aria-labelledby="signup-email-label"
          :class="{ error: errors.email }"
          :placeholder="t('ph_email')"
          inputmode="email"
          autocomplete="email"
          @input="$emit('update:email', $event.target.value)"
        />
        <div
          v-if="errors.email"
          class="err"
        >
          <span>{{ errors.email }}</span>
        </div>
        <div
          v-else
          class="field-note"
        >
          {{ t('helper_email') }}
        </div>
      </div>

      <div class="signup-field">
        <label
          class="field-label"
          id="signup-pw-label"
        >{{ t('lbl_password') }}</label
        >
        <div class="pw-wrap">
          <input
            id="signup-pw"
            :value="password"
            :type="showPw ? 'text' : 'password'"
            class="signup-input"
            aria-labelledby="signup-pw-label"
            :class="{ error: errors.password }"
            :placeholder="t('ph_password')"
            autocomplete="new-password"
            @input="$emit('update:password', $event.target.value)"
          />
          <button
            type="button"
            class="pw-toggle"
            :aria-label="t('lbl_password')"
            @click="showPw = !showPw"
          >
            <icon-eye :size="18" />
          </button>
        </div>
        <div
          class="strength"
          :class="'s' + pwStrength"
          aria-hidden="true"
        >
          <span class="bar"></span><span class="bar"></span><span class="bar"></span
        ><span class="bar"></span>
        </div>
        <div
          v-if="errors.password"
          class="err"
        >
          <span>{{ errors.password }}</span>
        </div>
        <div
          v-else
          class="field-note"
        >
          {{ t('helper_password') }}
        </div>
      </div>

      <label
        class="v-check req"
        :class="{ error: errors.terms }"
      >
        <input
          :checked="terms"
          type="checkbox"
          @change="$emit('update:terms', $event.target.checked)"
        />
        <span class="box"><icon-check /></span>
        <span>{{ trans.get('landing:signup.terms_check') }}</span>
      </label>
      <div
        v-if="errors.terms"
        class="err"
      >
        <span>{{ errors.terms }}</span>
      </div>
    </template>

    <div
      v-if="submitError"
      class="submit-err show"
    >
      <icon-info-circle
        :size="16"
        :stroke-width="2.4"
      />
      <span>{{ submitError }}</span>
    </div>
  </div>
</template>

<script>
import { i18nMixin, passwordStrengthScore } from '../helpers';
import IconCheck from '../../icons/IconCheck.vue';
import IconInfoCircle from '../../icons/IconInfoCircle.vue';
import IconEye from '../../icons/IconEye.vue';

export default {
  name: 'AccountStep',
  mixins: [i18nMixin],
  components: {
    IconCheck,
    IconInfoCircle,
    IconEye,
  },
  props: {
    name: { type: String, default: '' },
    email: { type: String, default: '' },
    password: { type: String, default: '' },
    terms: { type: Boolean, default: false },
    // Field-level validation errors from the parent's submit handler.
    errors: { type: Object, default: () => ({}) },
    // Top-level submit error message (non-field-specific).
    submitError: { type: String, default: '' },
    // Continue-mode: account already exists, so email/password/terms are
    // hidden and only the display name is collected here.
    isContinuing: { type: Boolean, default: false },
  },
  data() {
    // Password visibility is pure view state of this step. The parent gives the
    // step a fresh instance (via :key) at the start of each signup session, so
    // it never has to reach in to reset this.
    return {
      showPw: false,
    };
  },
  computed: {
    // 0..4 strength score — drives the .strength.sN class.
    pwStrength() {
      return passwordStrengthScore(this.password);
    },
  },
};
</script>
